import type React from "react";

import { Image, Text, View } from "@react-pdf/renderer";

import type { UserCourseExercise } from "api/schemas/UserCourse";

import { pdfStyles } from "./pdfStyles";
import type { PDFTranslations } from "./types";
import { getBestImageUrl, stripHTML } from "./utils";

export const ExerciseItem: React.FC<{
  exercise: UserCourseExercise;
  translations: PDFTranslations;
}> = ({ exercise, translations }) => {
  // some exercises might incorrectly have current_level set to 0
  const currentLevelIndex = exercise.current_level > 0 ? exercise.current_level - 1 : 0;
  const level = exercise.levels[currentLevelIndex];

  if (!level) {
    return (
      <View style={pdfStyles.exerciseCard}>
        <Text style={pdfStyles.errorText}>{translations.levelNotFound}</Text>
      </View>
    );
  }

  const {
    title,
    variation_title,
    image_url,
    video_thumbnail_file_url,
    about,
    reps,
    sets,
    tip_description,
    instructions_text1,
    instructions_text3,
  } = level;

  const imageUrl = getBestImageUrl(image_url, video_thumbnail_file_url);
  const fullTitle = variation_title ? `${title} - ${variation_title}` : title;

  // Build reps/sets info parts
  const infoParts: string[] = [];
  if (reps) infoParts.push(`${translations.reps}: ${reps}`);
  if (sets) infoParts.push(`${translations.sets}: ${sets}`);
  const repsInfo = infoParts.join("  ");

  // Build instructions parts
  const instructionsParts: string[] = [];
  if (instructions_text1) instructionsParts.push(stripHTML(instructions_text1));
  if (instructions_text3) instructionsParts.push(stripHTML(instructions_text3));
  const instructionsText = instructionsParts.join(" ");

  const fullInfo = [repsInfo, instructionsText].filter(Boolean).join("  |  ");

  return (
    <View style={pdfStyles.exerciseCard} wrap={false}>
      <View style={pdfStyles.exerciseContent}>
        {imageUrl && <Image style={pdfStyles.exerciseImage} src={imageUrl} cache={false} />}

        <View style={pdfStyles.exerciseDetails}>
          <Text style={pdfStyles.exerciseTitle}>{fullTitle}</Text>

          {fullInfo && <Text style={pdfStyles.compactRepsInfo}>{fullInfo}</Text>}

          {about && <Text style={pdfStyles.sectionContent}>{stripHTML(about)}</Text>}

          {tip_description && <Text style={pdfStyles.sectionContent}>{stripHTML(tip_description)}</Text>}
        </View>
      </View>
    </View>
  );
};
